import Konva from "konva";
import { KonvaEventObject } from "konva/lib/Node";
import { Vector2d } from "konva/lib/types";
import React, { RefObject, useCallback, useEffect, useMemo, useRef } from "react";
import { Group, Image as KonvaImage, Rect, Text } from "react-konva";

import { ImageSettings } from "context/ImageSettingsContext/imageSettingsContext";

import { Dimensions } from "../hooks/useResize";
import { BoundingBoxNode, Point } from "../types";
import { normalizedRotation } from "../utils";

// Memoize shadow props to avoid recalculating them on every render
const getShadowProps = (zoom: number) => ({
  shadowOffset: {
    x: 0.5 / zoom,
    y: 0.5 / zoom,
  },
  shadowBlur: 3 / zoom,
  shadowOpacity: 0.7,
  shadowColor: "black",
});

type Position = {
  x: number;
  y: number;
};

type Corner = "top-left" | "top-right" | "bottom-right" | "bottom-left";

type CornerNodeProps = {
  x: number;
  y: number;
  size: number;
  borderWidth: number;
  corner: Corner;
  fill: string;
  setHoveredNode: (node: BoundingBoxNode) => void;
  onDragMove?: (event: KonvaEventObject<DragEvent>) => void;
  onDragEnd?: () => void;
  hitStrokeWidth?: number;
  draggable?: boolean;
  offsetX?: number;
  offsetY?: number;
  dragBoundFunc?: (pos: Vector2d) => Vector2d;
};

const CORNER_TO_ROTATION_DEGREE: Record<Corner, number> = {
  "top-left": 0,
  "top-right": 90,
  "bottom-right": 180,
  "bottom-left": 270,
};

export function CornerNode({
  x,
  y,
  size,
  borderWidth,
  corner,
  fill,
  setHoveredNode,
  onDragMove,
  onDragEnd,
  hitStrokeWidth,
  draggable,
  offsetX = 0,
  offsetY = 0,
  dragBoundFunc,
}: CornerNodeProps) {
  const rotation = CORNER_TO_ROTATION_DEGREE[corner];
  const ref = useRef<Konva.Group | null>(null);

  return (
    <Group
      ref={ref}
      offsetX={offsetX}
      offsetY={offsetY}
      onMouseEnter={() => {
        setHoveredNode(corner);
      }}
      onMouseLeave={() => {
        setHoveredNode("none");
      }}
      draggable={draggable}
      onDragMove={onDragMove}
      onDragEnd={onDragEnd}
      x={x}
      y={y}
      dragBoundFunc={dragBoundFunc}
      perfectDrawEnabled={false}
      transformsEnabled="position"
    >
      <Rect
        width={borderWidth}
        height={size}
        cornerRadius={100}
        fill={fill}
        rotation={rotation}
        hitStrokeWidth={hitStrokeWidth}
      />
      <Rect
        width={size}
        height={borderWidth}
        cornerRadius={100}
        fill={fill}
        rotation={rotation}
        hitStrokeWidth={hitStrokeWidth}
      />
    </Group>
  );
}

type EdgeNodeProps = {
  x: number;
  y: number;
  width: number;
  height: number;
  fill: string;
  setHoveredNode: (node: BoundingBoxNode) => void;
  nodeType: "top-middle" | "right-middle" | "bottom-middle" | "left-middle";
  onDragMove?: (event: KonvaEventObject<DragEvent>) => void;
  onDragEnd?: () => void;
  hitStrokeWidth?: number;
  draggable?: boolean;
  offsetX?: number;
  offsetY?: number;
  dragBoundFunc?: (pos: Vector2d) => Vector2d;
};

export function EdgeNode({
  x,
  y,
  width,
  height,
  fill,
  setHoveredNode,
  nodeType,
  onDragMove,
  onDragEnd,
  hitStrokeWidth,
  draggable,
  offsetX = 0,
  offsetY = 0,
  dragBoundFunc,
}: EdgeNodeProps) {
  const ref = useRef<Konva.Rect | null>(null);

  return (
    <Rect
      ref={ref}
      x={x}
      y={y}
      width={width}
      height={height}
      offsetX={offsetX}
      offsetY={offsetY}
      fill={fill}
      cornerRadius={100}
      onMouseEnter={() => {
        setHoveredNode(nodeType);
      }}
      onMouseLeave={() => {
        setHoveredNode("none");
      }}
      draggable={draggable}
      onDragMove={onDragMove}
      onDragEnd={onDragEnd}
      dragBoundFunc={dragBoundFunc}
      hitStrokeWidth={hitStrokeWidth}
      perfectDrawEnabled={false}
      listening
      transformsEnabled="position"
    />
  );
}

type MiddleCrossProps = {
  color: string;
  center: Point;
  borderWidth: number;
  size: number;
  dragBoundFunc?: (pos: Vector2d) => Vector2d;
  hitStrokeWidth?: number;
  onDragMove?: (event: KonvaEventObject<DragEvent>) => void;
  onDragEnd?: () => void;
  draggable?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onMouseDown?: (event: KonvaEventObject<MouseEvent>) => void;
};

export function MiddleCross({
  color,
  center,
  borderWidth,
  size,
  hitStrokeWidth,
  draggable,
  onMouseEnter,
  onMouseLeave,
  onDragMove,
  onDragEnd,
  dragBoundFunc,
  onMouseDown,
}: MiddleCrossProps) {
  const shadowProps = useMemo(() => getShadowProps(1), []);

  return (
    <Group
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      x={center.x}
      y={center.y}
      {...(dragBoundFunc ? { dragBoundFunc } : {})}
      onDragMove={onDragMove}
      onDragEnd={onDragEnd}
      draggable={draggable}
      onMouseDown={onMouseDown}
      perfectDrawEnabled={false}
      transformsEnabled="position"
    >
      {/* SHADOW */}
      <Rect
        {...shadowProps}
        width={borderWidth}
        height={size}
        fill={color}
        cornerRadius={100}
        hitStrokeWidth={hitStrokeWidth}
        offsetX={borderWidth / 2}
        offsetY={size / 2}
      />

      <Rect
        {...shadowProps}
        offsetX={size / 2}
        offsetY={borderWidth / 2}
        width={size}
        height={borderWidth}
        fill={color}
        cornerRadius={100}
        hitStrokeWidth={hitStrokeWidth}
      />

      {/* NORMAL */}
      <Rect
        width={borderWidth}
        height={size}
        fill={color}
        cornerRadius={100}
        hitStrokeWidth={hitStrokeWidth}
        offsetX={borderWidth / 2}
        offsetY={size / 2}
      />

      <Rect
        offsetX={size / 2}
        offsetY={borderWidth / 2}
        width={size}
        height={borderWidth}
        fill={color}
        cornerRadius={100}
        hitStrokeWidth={hitStrokeWidth}
      />
    </Group>
  );
}

// Constants
const FLOATING_ICON_PADDING = 4.5;

export const Label = React.memo(
  ({
    label,
    position: initialPosition,
    color,
    imageSettings,
    onMouseEnter,
    onMouseLeave,
    draggable,
    innerPosition,
    onMouseDown,
    onDragMove,
    onDragEnd,
    dragBoundFunc,
    zoom,
    dimensions,
  }: {
    innerPosition?: boolean;
    label: string;
    position: Point;
    color: string;
    imageSettings?: ImageSettings;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
    dragBoundFunc?: (pos: Vector2d) => Vector2d;
    dimensions: {
      width: number;
      height: number;
    };
    imageRectRef: RefObject<Konva.Rect>;
    draggable?: boolean;
    onDragMove?: (event: KonvaEventObject<DragEvent>) => void;
    onDragEnd?: () => void;
    zoom: number;
    onMouseDown?: () => void;
  }) => {
    // Use static values for character width to avoid expensive calculations
    const CHAR_WIDTH = 6.5;
    const TEXT_HEIGHT = 13;
    const FONT_SIZE = 12;

    // Calculate text dimensions using a more efficient approach
    // This is a significant performance optimization
    const textWidth = label.length * (CHAR_WIDTH / zoom);
    const textHeight = TEXT_HEIGHT / zoom;

    // Pre-calculate all values to avoid recalculations in render
    const paddingAdjusted = FLOATING_ICON_PADDING / zoom;
    const rectWidth = textWidth + 2 * paddingAdjusted;
    const offsetX = innerPosition ? rectWidth + 2 * paddingAdjusted : rectWidth;
    const offsetY = innerPosition ? textHeight + textHeight / 2 : -textHeight - zoom / 8;

    // Memoize shadow props to avoid recalculation
    const shadowProps = useMemo(() => getShadowProps(1), []);

    const getRotatedPosition = useCallback(
      (position: Position, dimensions: Dimensions, rotationValue: number, flipX: boolean, flipY: boolean): Position => {
        const { x, y } = position;
        const { width, height } = dimensions;
        if (rotationValue === 0) {
          if (!flipX && !flipY) return { x: x + width, y: y + height };
          if (flipX && !flipY) return { x, y: y + height };
          if (!flipX && flipY) return { x: x + width, y };
        }
        if (rotationValue === 90) {
          if (!flipX && !flipY) return { x: x + width, y };
          if (flipX && !flipY) return { x, y };
          if (!flipX && flipY) return { x: x + width, y: y + height };
          if (flipX && flipY) return { x, y: y + height };
        }

        if (rotationValue === 180) {
          if (flipX && !flipY) return { x: x + width, y };
          if (!flipX && flipY) return { x, y: y + height };
          if (flipX && flipY) return { x: x + width, y: y + height };
        }

        if (rotationValue === 270) {
          if (flipX && !flipY) return { x: x + width, y: y + height };
          if (flipX && flipY) return { x: x + width, y };
          if (!flipX && !flipY) return { x, y: y + height };
        }

        return { x, y };
      },
      []
    );

    const rotation = useMemo(
      () =>
        imageSettings ? normalizedRotation(imageSettings.rotation ?? 0, imageSettings.flipX, imageSettings.flipY) : 0,
      [imageSettings]
    );

    const position = useMemo(
      () =>
        getRotatedPosition(
          initialPosition,
          dimensions,
          imageSettings?.rotation ?? 0,
          imageSettings?.flipX ?? false,
          imageSettings?.flipY ?? false
        ),
      [
        dimensions,
        getRotatedPosition,
        imageSettings?.flipX,
        imageSettings?.flipY,
        imageSettings?.rotation,
        initialPosition,
      ]
    );

    return (
      <Group
        rotation={rotation}
        x={position.x}
        y={position.y}
        offsetX={offsetX}
        offsetY={offsetY}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onDragMove={onDragMove}
        onDragEnd={onDragEnd}
        dragBoundFunc={dragBoundFunc}
        draggable={draggable}
        perfectDrawEnabled={false} // Disable perfect drawing for performance
        scaleX={imageSettings?.flipX ? -1 : 1}
        scaleY={imageSettings?.flipY ? -1 : 1}
        onMouseDown={onMouseDown}
      >
        <Rect
          cornerRadius={100}
          stroke={color}
          fill={color}
          width={rectWidth}
          height={textHeight}
          {...shadowProps}
          strokeScaleEnabled={false}
        />
        <Text
          text={label}
          fontSize={FONT_SIZE / zoom}
          x={paddingAdjusted}
          verticalAlign="middle"
          perfectDrawEnabled={false}
          fillEnabled // Enable only necessary features
          strokeEnabled={false}
          shadowEnabled={false}
        />
      </Group>
    );
  },
  // Custom comparison function to prevent unnecessary re-renders
  (prevProps, nextProps) =>
    prevProps.label === nextProps.label &&
    prevProps.position.x === nextProps.position.x &&
    prevProps.position.y === nextProps.position.y &&
    prevProps.dimensions.width === nextProps.dimensions.width &&
    prevProps.dimensions.height === nextProps.dimensions.height &&
    prevProps.color === nextProps.color &&
    prevProps.zoom === nextProps.zoom &&
    prevProps.innerPosition === nextProps.innerPosition &&
    prevProps.draggable === nextProps.draggable &&
    prevProps.imageSettings?.flipX === nextProps.imageSettings?.flipX &&
    prevProps.imageSettings?.flipY === nextProps.imageSettings?.flipY &&
    prevProps.onMouseEnter === nextProps.onMouseEnter &&
    prevProps.onMouseLeave === nextProps.onMouseLeave &&
    prevProps.imageSettings?.rotation === nextProps.imageSettings?.rotation
);

export const DeleteIcon = React.memo(
  ({
    position: initialPosition,
    dimensions,
    onDelete,
    color,
    isDragging,
    onMouseEnter,
    onMouseLeave,
    innerPosition,
    zoom,
    imageSettings,
  }: {
    position: Point;
    dimensions: Dimensions;
    onDelete?: () => void;
    color: string;
    isDragging?: boolean;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
    innerPosition?: boolean;
    zoom: number;
    imageSettings?: ImageSettings;
  }) => {
    /**
     * Returns an SVG representation of the "delete forever" icon.
     * This is memoized to avoid recreating the SVG string on every render
     */
    const getDeleteForeverSVG = useCallback(
      (iconColor: string): string => `
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zm2.46-7.12 1.41-1.41L12 12.59l2.12-2.12 1.41 1.41L13.41 14l2.12 2.12-1.41 1.41L12 15.41l-2.12 2.12-1.41-1.41L10.59 14l-2.13-2.12zM15.5 4l-1-1h-5l-1 1H5v2h14V4z" fill="${iconColor}"></path>
        </svg>
      `,
      []
    );

    // Create icon image immediately
    const deleteImage = useMemo(() => {
      const image = new window.Image();
      const svg = encodeURIComponent(getDeleteForeverSVG(color));
      image.src = `data:image/svg+xml;utf8,${svg}`;
      return image;
    }, [color, getDeleteForeverSVG]);

    // Ensure the image is loaded by the time we need to render
    useEffect(() => {
      if (!deleteImage.complete) {
        deleteImage.onload = () => {
          // Find and redraw the Konva layer to ensure the icon is visible
          const stage = Konva.stages[0];
          if (stage) {
            const layers = stage.getLayers();
            layers.forEach((layer) => layer.batchDraw());
          }
        };
      }
    }, [deleteImage]);

    // Memoize the icon size calculation
    const iconSquareSize = useMemo(() => 24 / zoom, [zoom]);

    // Memoize shadow props
    const shadowProps = useMemo(() => getShadowProps(1), []);

    const getDeleteIconPosition = useCallback(
      (position: Position, dimensions: Dimensions, rotationValue: number, flipX: boolean, flipY: boolean): Position => {
        const { x, y } = position;
        const { width, height } = dimensions;
        // default values
        let labelX = x;
        let labelY = y;

        if (rotationValue === 0) {
          if (!flipX && !flipY) labelX = x + width;
          else if (flipX && flipY) labelY = y + height;
          else if (flipY) {
            labelX = x + width;
            labelY = y + height;
          }
        }

        if (rotationValue === 90) {
          if (flipX && !flipY) labelX = x + width;
          else if (!flipX && flipY) labelY = y + height;
          else if (flipX && flipY) {
            labelX = x + width;
            labelY = y + height;
          }
        }

        if (rotationValue === 180) {
          if (!flipX && !flipY) labelY = y + height;
          else if (flipX && !flipY) {
            labelX = x + width;
            labelY = y + height;
          } else if (flipX && flipY) {
            labelX = x + width;
          }
        }

        if (rotationValue === 270) {
          if (!flipX && !flipY) {
            labelX = x + width;
            labelY = y + height;
          } else if (flipX && !flipY) labelY = y + height;
          else if (!flipX && flipY) labelX = x + width;
        }

        return { x: labelX, y: labelY };
      },
      []
    );

    const position = useMemo(
      () =>
        getDeleteIconPosition(
          initialPosition,
          dimensions,
          imageSettings?.rotation ?? 0,
          imageSettings?.flipX ?? false,
          imageSettings?.flipY ?? false
        ),
      [
        dimensions,
        getDeleteIconPosition,
        imageSettings?.flipX,
        imageSettings?.flipY,
        imageSettings?.rotation,
        initialPosition,
      ]
    );
    const rotation = useMemo(
      () =>
        imageSettings?.rotation !== undefined
          ? normalizedRotation(imageSettings.rotation ?? 0, imageSettings.flipX, imageSettings.flipY)
          : 0,
      [imageSettings?.flipX, imageSettings?.flipY, imageSettings?.rotation]
    );
    return (
      <KonvaImage
        {...shadowProps}
        rotation={rotation}
        image={deleteImage}
        width={iconSquareSize}
        height={iconSquareSize}
        x={position.x}
        y={position.y}
        scaleX={imageSettings?.flipX ? -1 : 1}
        scaleY={imageSettings?.flipY ? -1 : 1}
        offsetY={innerPosition ? -FLOATING_ICON_PADDING : iconSquareSize + FLOATING_ICON_PADDING / zoom}
        offsetX={innerPosition ? iconSquareSize + FLOATING_ICON_PADDING : iconSquareSize - FLOATING_ICON_PADDING / zoom}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onMouseDown={(event) => {
          if (isDragging) return false;
          // eslint-disable-next-line no-param-reassign
          event.cancelBubble = true;
          onDelete?.();
          return false;
        }}
      />
    );
  }
);

export function SelectedGradient({ dimensions, position }: { dimensions: Dimensions; position: Point }) {
  return (
    <Rect
      width={dimensions.width}
      height={dimensions.height}
      x={position.x}
      y={position.y}
      fill="rgba(255, 255, 255, 0.2)"
    />
  );
}
